import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Wallet, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { KpiGrid, SectionHeader, ExportButtons, ReportTable, fIDR, fIDRCompact, fDateID } from './shared';

export function PaymentsTab({ data, loading, filters }) {
  const navigate = useNavigate();
  const s = data?.summary || {};
  const cards = [
    { label: 'Total Pembayaran', value: fIDRCompact(s.total_paid), accent: 'success' },
    { label: 'Jumlah Transaksi', value: s.payment_count || 0, accent: '' },
    { label: 'Dengan Bukti Transfer', value: s.with_proof_count || 0, accent: 'success' },
    { label: 'Tanpa Bukti Transfer', value: s.without_proof_count || 0, accent: 'warn' },
    { label: 'Transfer Bank', value: fIDRCompact(s.total_bank_transfer), accent: 'primary' },
    { label: 'Cash', value: fIDRCompact(s.total_cash), accent: '' },
    { label: 'QRIS', value: fIDRCompact(s.total_qris), accent: '' },
    { label: 'Lainnya', value: fIDRCompact(s.total_other), accent: '' },
  ];
  const rows = data?.rows || [];
  return (
    <div className="space-y-4">
      <SectionHeader icon={Wallet} title="Laporan Pembayaran" action={<ExportButtons tab="payments" filters={filters} />} />
      <KpiGrid items={cards} loading={loading} columns={4} />
      <Card className="bg-card border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-100">Detail Pembayaran ({rows.length})</CardTitle></CardHeader>
        <CardContent className="p-0 sm:p-4">
          <ReportTable
            getRowKey={(r) => r.id}
            columns={[
              { key: 'payment_date', label: 'Tgl Bayar', render: r => <span className="text-slate-200">{fDateID(r.payment_date)}</span> },
              { key: 'invoice_number', label: 'No Invoice', render: r => <span className="font-mono-num text-slate-100">{r.invoice_number}</span> },
              { key: 'client_name', label: 'Client', render: r => <span className="font-medium text-slate-100">{r.client_name}</span> },
              { key: 'event_date', label: 'Tgl Event', render: r => <span className="text-slate-300">{fDateID(r.event_date)}</span> },
              { key: 'venue', label: 'Venue', render: r => <span className="text-slate-400 truncate" title={r.venue}>{r.venue || '—'}</span> },
              { key: 'method', label: 'Metode', render: r => <span className="text-slate-200">{r.method || '—'}</span> },
              { key: 'amount', label: 'Nominal', align: 'right', render: r => <span className="font-mono-num text-emerald-300">{fIDR(r.amount)}</span> },
              { key: 'reference_number', label: 'Referensi', render: r => <span className="text-xs text-slate-400 font-mono-num">{r.reference_number || '—'}</span> },
              { key: 'has_proof', label: 'Bukti TF', render: r => <span className={`text-xs ${r.has_proof ? 'text-emerald-300' : 'text-amber-300'}`}>{r.has_proof ? 'Ada' : 'Belum'}</span> },
              { key: 'recorded_by', label: 'Dicatat Oleh', render: r => <span className="text-xs text-slate-300">{r.recorded_by || '—'}</span> },
              { key: 'action', label: 'Aksi', align: 'right', render: r => (
                <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${r.invoice_id}`); }} data-testid={`report-payment-detail-${r.id}`}>
                  <ExternalLink className="h-3 w-3" />
                </Button>
              ) },
            ]}
            rows={rows}
            emptyText="Tidak ada pembayaran pada periode ini."
            mobileCard={(r) => (
              <div className="text-sm space-y-1" onClick={() => navigate(`/invoices/${r.invoice_id}`)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-100 truncate">{r.client_name}</div>
                    <div className="text-xs text-slate-300 font-mono-num">{r.invoice_number}</div>
                  </div>
                  <span className={`text-xs ${r.has_proof ? 'text-emerald-300' : 'text-amber-300'} font-medium`}>{r.has_proof ? 'Bukti Ada' : 'Belum'}</span>
                </div>
                <div className="grid grid-cols-2 gap-1 text-xs text-slate-300 mt-2">
                  <div>Tgl: {fDateID(r.payment_date)}</div>
                  <div>Metode: {r.method || '—'}</div>
                </div>
                <div className="font-mono-num font-semibold text-emerald-300 mt-1">{fIDR(r.amount)}</div>
              </div>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}
